import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService, hrErrorMessage } from '../hr.service';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { HrEmployee, hrColorOf, hrEmployeeNameOf, hrIsAdmin, hrIsHrManager } from '@app/shared/_models/hr';
import { C_value, Store, User } from '@app/shared/_models/common';
import { C_VALUES_HABILITATION, C_VALUES_USER_ROLE, EVENTTYP_UPDATE, HABILITATION_HR_MANAGER, HR_PERSON_COLORS, OBJECTTYP_HR_EMPLOYEE } from '@app/shared/constants';

// Une ligne de la matrice : un employé, ses rôles MÉTIER (hr_employee.roles), ses HABILITATIONS
// (c_user.habilitations) et sa mobilité (hr_employee.mobility).
//
// Les deux familles de cases n'écrivent NI la même table NI sous la même garde, et c'est le sens
// même de la scission du 2026-08-03 : un rôle décrit un savoir-faire (décision RH), une
// habilitation ouvre des données (décision d'administrateur).
export interface StaffRow {
	employee: HrEmployee;
	id_user: number;
	name: string;
	color: string;
	roles: Set<number>;
	habilitations: Set<number>;
	mobility: Set<number>;
	active: boolean;
	saving: boolean;
}

// Personnel & rôles — /hr/staff. LA vue de paramétrage des personnes : une ligne par employé,
// des cases pour donner/retirer chaque rôle (écrit sur le c_user) et chaque établissement de
// mobilité (écrit sur la fiche hr_employee). Chaque case cochée est enregistrée immédiatement.
@Component({
	selector: 'app-hr-staff',
	standalone: true,
	imports: [MenuCommonComponent, MatTableModule, MatFormFieldModule, MatInputModule,
		MatButtonModule, MatIconModule, MatCheckboxModule, MatTooltipModule],
	templateUrl: './staff.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './staff.component.scss',
})
export class StaffComponent implements OnInit {

	public hrService = inject(HrService);
	private commonService = inject(CommonService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);
	private router = inject(Router);

	public rows: StaffRow[] = [];
	public shown: StaffRow[] = [];
	public roles: C_value[] = [];
	public stores: Store[] = [];
	// Responsable RH (habilitation 1) : il tient la matrice des RÔLES MÉTIER et la mobilité —
	// les deux s'écrivent sur la fiche salarié, sous la garde user_is_hr_manager.
	public isAdmin = false;
	// Administrateur (habilitation 0) : il tient la colonne des HABILITATIONS, et elle seule.
	// process_user_update_habilitations refuse les autres en 403 — les cases sont désactivées
	// plutôt que de proposer un geste voué à l'échec.
	public isAppAdmin = false;
	public habilitations: C_value[] = [];
	public readonly HR_MANAGER_HABILITATION = HABILITATION_HR_MANAGER;
	public loaded = false;
	// La matrice s'ouvre sur les seuls actifs (comme /hr/employees) mais les inactifs restent
	// ATTEIGNABLES : filtrer en dur donnait une page vide dès qu'une équipe avait été archivée.
	public activeOnly = true;
	private filter = '';

	ngOnInit(): void {
		this.load();
	}

	private load(): void {
		forkJoin({
			employees: this.hrService.getEmployees$(),
			users: this.hrService.getUsers$(),
			stores: this.commonService.getStores(),
			// Référentiel c_values : valuesOf$ porte les deux pièges du forkJoin (flux qui ne
			// complète jamais, échec qui fige l'écran) — cf. hr.service.ts.
			roles: this.hrService.valuesOf$(C_VALUES_USER_ROLE),
			habilitations: this.hrService.valuesOf$(C_VALUES_HABILITATION),
		}).subscribe(({ employees, users, stores, roles, habilitations }) => {
			// La base fait foi : un JWT émis avant la scission ne porte pas encore `habilitations`.
			this.isAdmin = hrIsHrManager(this.loginService.loggeduser, users);
			this.isAppAdmin = hrIsAdmin(this.loginService.loggeduser, users);
			this.stores = stores;
			this.roles = (roles as C_value[]).filter(r => r.is_active !== false);
			this.habilitations = (habilitations as C_value[]).filter(h => h.is_active !== false);
			this.rows = employees
				.map(e => this.toRow(e, users))
				.sort((a, b) => a.name.localeCompare(b.name));
			this.applyFilterNow();
			this.loaded = true;
		});
	}

	private toRow(e: HrEmployee, users: User[]): StaffRow {
		const user = users.find(u => u.id === e.id_user);
		return {
			employee: e,
			id_user: e.id_user ?? 0,
			name: hrEmployeeNameOf(e, users),
			color: hrColorOf(e.id, HR_PERSON_COLORS),
			// Rôles MÉTIER sur la FICHE, habilitations sur le COMPTE : deux tables, deux gardes.
			roles: new Set(e.roles ?? []),
			habilitations: new Set(user?.habilitations ?? []),
			mobility: new Set(e.mobility ?? (e.id_store != null ? [e.id_store] : [])),
			active: e.is_active !== false,
			saving: false,
		};
	}

	applyFilter(event: Event): void {
		this.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
		this.applyFilterNow();
	}

	onActiveToggle(checked: boolean): void {
		this.activeOnly = checked;
		this.applyFilterNow();
	}

	private applyFilterNow(): void {
		this.shown = this.rows.filter(r => (!this.activeOnly || r.active)
			&& (!this.filter || r.name.toLowerCase().includes(this.filter)));
	}

	// Nombre de fiches masquées par la case « Actifs seulement » — sans ce compteur, une équipe
	// entièrement archivée donne une page vide sans explication.
	get hiddenCount(): number {
		return this.activeOnly ? this.rows.filter(r => !r.active).length : 0;
	}

	// ============================================================
	// Rôles MÉTIER (hr_employee.roles) — case cochée = enregistrée
	// ============================================================

	// Décrire ce qu'une personne sait faire est un acte de GESTION DU PERSONNEL : il suffit d'être
	// responsable RH. Depuis la scission du 2026-08-03 il n'y a plus de cas particulier — aucun
	// rôle métier n'ouvre de données, il n'y a donc plus de case à protéger dans cette matrice.
	canToggleRole(_roleId: number): boolean {
		return this.isAdmin;
	}

	roleTooltip(role: C_value): string {
		if (!this.isAdmin) return (role.name ?? '') + ' — modification réservée au responsable RH.';
		return role.name ?? '';
	}

	onToggleRole(row: StaffRow, roleId: number): void {
		if (!this.canToggleRole(roleId)) return;
		const next = new Set(row.roles);
		if (next.has(roleId)) next.delete(roleId); else next.add(roleId);
		row.saving = true;
		this.hrService.postEmployeeRoles(row.employee, Array.from(next).sort((a, b) => a - b)).subscribe({
			next: () => { row.roles = next; row.employee.roles = Array.from(next); row.saving = false; },
			error: (e) => { row.saving = false; this.alertService.error(hrErrorMessage(e)); },
		});
	}

	// ============================================================
	// Habilitations (c_user.habilitations) — case cochée = enregistrée
	// ============================================================

	// Distribuer des DROITS est un acte d'administration : miroir de la garde serveur de
	// process_user_update_habilitations. Un responsable RH qui n'est pas administrateur voit la
	// colonne, mais ses cases sont mortes — le serveur refuserait en 403.
	canToggleHabilitation(): boolean {
		return this.isAppAdmin;
	}

	habilitationTooltip(h: C_value): string {
		if (!this.isAppAdmin) return (h.name ?? '') + ' — distribution des habilitations réservée aux administrateurs.';
		return h.id === HABILITATION_HR_MANAGER
			? 'Responsable RH — accès complet aux fiches, salaires, congés et heures de tout le personnel.'
			: (h.name ?? '');
	}

	// Une habilitation se pose sur un COMPTE : une personne sans c_user n'en reçoit pas.
	// En pratique la fiche salarié impose toujours un id_user, mais la matrice affiche aussi des
	// lignes archivées — mieux vaut un bouton mort qu'un event qui part sur l'id 0.
	onToggleHabilitation(row: StaffRow, habId: number): void {
		if (!this.canToggleHabilitation() || !row.id_user) return;
		const next = new Set(row.habilitations);
		if (next.has(habId)) next.delete(habId); else next.add(habId);
		row.saving = true;
		this.hrService.postUserHabilitations(row.id_user, Array.from(next).sort((a, b) => a - b)).subscribe({
			next: () => { row.habilitations = next; row.saving = false; },
			error: (e) => { row.saving = false; this.alertService.error(hrErrorMessage(e)); },
		});
	}

	// ============================================================
	// Mobilité (hr_employee.mobility) — l'établissement par défaut reste toujours coché
	// ============================================================

	isDefaultStore(row: StaffRow, storeId: number): boolean {
		return row.employee.id_store === storeId;
	}

	onToggleMobility(row: StaffRow, storeId: number): void {
		if (this.isDefaultStore(row, storeId)) return;		// le magasin par défaut ne se décoche pas ici
		const next = new Set(row.mobility);
		if (next.has(storeId)) next.delete(storeId); else next.add(storeId);
		if (row.employee.id_store != null) next.add(row.employee.id_store);
		const payload = this.hrService.buildEmployeePayload({ ...row.employee, mobility: Array.from(next).sort((a, b) => a - b) });
		row.saving = true;
		this.hrService.postHrEvent(EVENTTYP_UPDATE, OBJECTTYP_HR_EMPLOYEE, payload).subscribe({
			next: () => { row.mobility = next; row.employee.mobility = Array.from(next); row.saving = false; },
			error: (e) => { row.saving = false; this.alertService.error(hrErrorMessage(e)); },
		});
	}

	// ============================================================
	// Navigation
	// ============================================================

	onOpenEmployee(row: StaffRow): void {
		this.router.navigate(['/hr/employee/' + row.employee.id]);
	}

	onNew(): void {
		this.router.navigate(['/hr/employee/0']);
	}
}
